// User-friendly display names for scanner modules
// Maps backend scanner IDs to human-readable names for better UX

export const SCANNER_DISPLAY_NAMES: Record<string, string> = {
  // Core Security Scanners
  'secrets': 'API Keys & Secrets',
  'hardcoded_credentials': 'Hardcoded Passwords',
  'insecure_defaults': 'Insecure Configuration',
  'sdk_exposure': 'Frontend SDK Exposure',
  'unrestricted_api': 'Unprotected APIs',
  'rbac_misconfig': 'Access Control Issues',
  
  // Privacy Scanners  
  'privacy_log': 'Sensitive Data in Logs',
  'public_bucket': 'Public Storage Buckets',
  'supabase_cors': 'Database CORS Issues',
  
  // Compliance Scanners
  'rls_validator': 'Database Security Policies',
  'netlify_headers': 'Security Headers',
  'legal_presence': 'Legal Compliance',
  
  // Premium Edge Scanners
  'header_tls_auditor': 'HTTPS & TLS Security',
  'semgrep_lite': 'Code Security Analysis',
  'package_cve_checker': 'Package Vulnerabilities',
  'cookie_storage_linter': 'Token Storage Security',
  'ct_log_enumerator': 'Certificate Monitoring',
  
  // Advanced Security Scanners - Phase 2
  'api_security_comprehensive': 'API Security Analysis',
  'client_side_security': 'Frontend Security',
  'dependency_security_deep': 'Dependency Security',
  'framework_security_analyzer': 'Framework Security',
  'environment_config_security': 'Config Security',
  'database_security_deep': 'Database Security'
};

/**
 * Get user-friendly display name for a scanner
 * Falls back to a formatted version of the scanner key if no mapping exists
 */
export function getScannerDisplayName(scannerKey: string): string {
  if (SCANNER_DISPLAY_NAMES[scannerKey]) {
    return SCANNER_DISPLAY_NAMES[scannerKey];
  }
  
  // Fallback: format the scanner key to be more readable
  return scannerKey
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Get user-friendly description for common scanner types
 */
export function getScannerDescription(scannerKey: string): string {
  const descriptions: Record<string, string> = {
    'secrets': 'Finds exposed API keys, tokens, and credentials in your code',
    'hardcoded_credentials': 'Detects passwords and auth tokens hardcoded in source files',
    'insecure_defaults': 'Identifies debug settings and weak configurations',
    'sdk_exposure': 'Checks for client-side SDK usage without proper backend validation',
    'unrestricted_api': 'Finds API endpoints missing authentication or authorization',
    'rbac_misconfig': 'Validates role-based access control implementation',
    'privacy_log': 'Scans for personal data accidentally logged to console or files',
    'public_bucket': 'Detects cloud storage buckets with public access',
    'supabase_cors': 'Checks database CORS policies for security issues',
    'rls_validator': 'Validates Row Level Security policies in your database',
    'netlify_headers': 'Ensures proper security headers are configured',
    'legal_presence': 'Checks for required privacy policies and legal notices',
    'header_tls_auditor': 'Analyzes HTTPS configuration and TLS certificate security',
    'semgrep_lite': 'Advanced static analysis for common security vulnerabilities',
    'package_cve_checker': 'Scans dependencies for known security vulnerabilities',
    'cookie_storage_linter': 'Validates secure storage of authentication tokens',
    'ct_log_enumerator': 'Monitors certificate transparency logs for unauthorized certificates',
    'api_security_comprehensive': 'Detects OWASP API Top 10 vulnerabilities including broken authentication and injection attacks',
    'client_side_security': 'Scans for XSS, mixed content, CSP bypasses, and browser storage security issues',
    'dependency_security_deep': 'Advanced dependency scanner analyzing for CVEs, supply chain attacks, and integrity verification',
    'framework_security_analyzer': 'Framework-specific security scanner for React, Vue, Angular, and Svelte vulnerabilities',
    'environment_config_security': 'Comprehensive scanner for Docker, Kubernetes, Terraform, and CI/CD misconfigurations',
    'database_security_deep': 'Advanced database security scanner detecting SQL injection and ORM misconfigurations'
  };
  
  return descriptions[scannerKey] || 'Identifies potential security issues in your codebase';
} 